import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useOfflineStatus } from '@/contexts/OfflineContext';
import { OFFLINE_CONSTANTS } from '@/constants/offline';

/**
 * Component to handle routing based on network status
 * Redirects to offline page when connection is lost
 * Saves critical data for offline access
 */
export const OfflineRouter: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isOnline } = useOfflineStatus();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Cache current data for offline use
  const saveOfflineData = () => {
    try {
      // Get medication data from localStorage or other storage
      const currentProfileId = localStorage.getItem('currentProfileId') || 'u1';
      const medicationsKey = `medications_${currentProfileId}`;
      const medicationsData = localStorage.getItem(medicationsKey);
      
      // Create a data object with essential information for offline use
      const offlineData = {
        lastUpdated: new Date().toISOString(),
        currentProfileId,
        medications: medicationsData ? JSON.parse(medicationsData) : [],
        // Add other essential data here as needed
      };
      
      // Save to localStorage for offline access
      localStorage.setItem(OFFLINE_CONSTANTS.OFFLINE_DATA_KEY, JSON.stringify(offlineData));
      
    } catch (error) {
      
    }
  };
  
  useEffect(() => {
    // Only redirect if we're not already on the offline page and user is offline
    if (!isOnline && location.pathname !== '/offline') {
      
      
      // Save current state before redirecting
      saveOfflineData();
      
      // Navigate to offline page
      navigate('/offline', { replace: true });
    }
  }, [isOnline, location.pathname, navigate]);
  
  // If we're online and not on a critical path, periodically save data for offline use
  useEffect(() => {
    if (isOnline && !OFFLINE_CONSTANTS.CRITICAL_OFFLINE_PATHS.includes(location.pathname)) {
      // Save current state for offline use
      saveOfflineData();
    }
  }, [isOnline, location.pathname]);
  
  return <>{children}</>;
};

export default OfflineRouter;
